import type { MonetaryValue } from 'ezmoney';
import * as ezmoney from 'ezmoney';

import type { CostCenter } from 'modules/budgets/models/costCenter';
import { type PurchaseOrderRequestItem } from 'modules/requests/models/purchaseOrderRequest';
import { type GetV2ApiCompanyIdPurchaseOrdersPurchaseOrderIdThreeWayMatchLinesSummaryResponseOK } from 'src/client/spendesk-api-types';

import { type DeliveryNotesStatuses } from './deliveryNote';
import { type Supplier } from './supplier';
import { type Team } from './team';
import { type User } from './user';
import { type LineItem } from '../../requests/invoice/creation/ManageLineItemsModal/types';
import { type Invoice } from '../detail/models';

export type PurchaseOrderStatus = 'open' | 'closed' | 'cancelled';

export type PurchaseOrderId = string;

export type BillingStatus =
  | 'waitingForBilling'
  | 'lateInvoice'
  | 'partiallyBilled'
  | 'billed'
  | 'overbilled';

export type BillingOverDeliveryStatus =
  | 'overbilled'
  | 'underbilled'
  | 'fullybilled'
  | 'none';

interface PurchaseOrderBase {
  id: PurchaseOrderId;
  amount: MonetaryValue;
  netAmount?: MonetaryValue | null;
  billedAmount: MonetaryValue; // includes all invoice requests created or approved (does not include inbox invoices)
  deliveredAmount: MonetaryValue; // ∑ item unit price * qty delivered
  purchaseOrderNumber: string;
  description: string;
  status: PurchaseOrderStatus;
  billingStatus?: BillingStatus;
  deliveryStatus?: DeliveryNotesStatuses;
  billingOverDeliveryStatus?: BillingOverDeliveryStatus;
  startDate: string;
  endDate: string;
  createdAt: string;
  supplier: Supplier;
  requester: User;
  requestId: string;
  team?: Team;
  costCenter?: CostCenter;
  items?: PurchaseOrderRequestItem[];
  areInvoiceLineItemsRequired: boolean;
}

export type PurchaseOrderChangedValue =
  | {
      fieldType: 'string';
      oldValue?: string;
      newValue?: string;
    }
  | {
      fieldType: 'date';
      oldValue?: string;
      newValue?: string;
    }
  | {
      fieldType: 'monetaryValue';
      oldValue?: MonetaryValue;
      newValue?: MonetaryValue;
    }
  | {
      fieldType: 'boolean';
      oldValue?: boolean;
      newValue?: boolean;
    };

export type PurchaseOrderDataChange = {
  fieldKey?:
    | 'description'
    | 'startDate'
    | 'endDate'
    | 'teamId'
    | 'costCenter'
    | 'amount'
    | 'netAmount'
    | 'customFieldAssociations'
    | 'itemLines'
    | 'analyticalSplits';
  fieldDisplayLabel?: string;
  lineItemIndex?: number;
} & PurchaseOrderChangedValue;
export interface OpenedPurchaseOrder extends PurchaseOrderBase {
  status: 'open';
  billingStatus:
    | 'waitingForBilling'
    | 'lateInvoice'
    | 'partiallyBilled'
    | 'billed'
    | 'overbilled';
  deliveryStatus: NonNullable<DeliveryNotesStatuses>;
  billingOverDeliveryStatus: NonNullable<BillingOverDeliveryStatus>;
}
export interface ClosedPurchaseOrder extends PurchaseOrderBase {
  status: 'closed';
  billingStatus: 'partiallyBilled' | 'billed';
  deliveryStatus: NonNullable<DeliveryNotesStatuses>;
  billingOverDeliveryStatus: NonNullable<BillingOverDeliveryStatus>;
}

export interface CancelledPurchaseOrder extends PurchaseOrderBase {
  status: 'cancelled';
  billingStatus: undefined;
}

export type PurchaseOrder =
  | OpenedPurchaseOrder
  | ClosedPurchaseOrder
  | CancelledPurchaseOrder;

export type CustomFieldNameValue = {
  name: string;
  value: string;
};

export interface PurchaseOrderExportLineItem {
  name: string;
  quantity: number;
  unitPriceExclTaxes: MonetaryValue;
  unitPriceInclTaxes: MonetaryValue;
  vatRate?: number;
  totalExclTaxes: MonetaryValue;
  totalInclTaxes: MonetaryValue;
  numberOfItemsReceived: number;
  numberOfItemsBilled: number;
}

export type PurchaseOrderExport = PurchaseOrder & {
  procurementPurchaseOrderId?: string;
  currency: string;
  customFieldsNameValue: CustomFieldNameValue[];
  selectedExpenseCategoryValue?: string;
  invoices?: Invoice[];
  requester: {
    email: string;
  };
  company: {
    name: string;
  };
  lineItems?: PurchaseOrderExportLineItem[];
};

export type LineItemSummaries = Omit<
  GetV2ApiCompanyIdPurchaseOrdersPurchaseOrderIdThreeWayMatchLinesSummaryResponseOK,
  'purchaseOrderLines'
> & {
  purchaseOrderLines: Array<
    {
      invoices: Array<
        {
          tag?: 'currentInvoice';
          invoiceNumber: string | undefined | null;
          invoiceRequestId: string | undefined | null;
          requestId: string | undefined | null;
        } & Omit<
          GetV2ApiCompanyIdPurchaseOrdersPurchaseOrderIdThreeWayMatchLinesSummaryResponseOK['purchaseOrderLines'][number]['invoices'][number],
          'invoiceNumber' | 'invoiceRequestId' | 'requestId'
        >
      >;
    } & Omit<
      GetV2ApiCompanyIdPurchaseOrdersPurchaseOrderIdThreeWayMatchLinesSummaryResponseOK['purchaseOrderLines'][number],
      'invoices'
    >
  >;
};

export const getPurchaseOrderAvailableAmount = (
  purchaseOrder: PurchaseOrder,
): MonetaryValue =>
  ezmoney.subtract(purchaseOrder.amount, purchaseOrder.billedAmount);

export const getCanAttachInvoices = (purchaseOrder: PurchaseOrder): boolean =>
  purchaseOrder.status === 'open';

export const getIsCancelledPurchaseOrder = (
  purchaseOrder: PurchaseOrder,
): purchaseOrder is CancelledPurchaseOrder =>
  purchaseOrder.status === 'cancelled';

export const computeItemBillingStatus = (
  itemDetails: Pick<
    LineItemSummaries['purchaseOrderLines'][number],
    | 'unitPriceAmount'
    | 'quantity'
    | 'quantityBilled'
    | 'amountBilledWithTax'
    | 'vatRate'
    | 'currency'
  >,
): BillingStatus => {
  const quantityWithDecimals = ezmoney.fromNumber(
    itemDetails.quantity,
    itemDetails.currency,
    4,
  );
  const expectedBilledAmountWithoutTax = ezmoney.multiply(
    itemDetails.unitPriceAmount,
    quantityWithDecimals.amount,
    quantityWithDecimals.precision,
  );

  const taxFactor = itemDetails.vatRate ? 1 + itemDetails.vatRate / 100 : 1;
  const taxFactorAmount = ezmoney.fromNumber(
    taxFactor,
    itemDetails.currency,
    4,
  );

  const expectedBilledAmount = ezmoney.multiply(
    expectedBilledAmountWithoutTax,
    taxFactorAmount.amount,
    taxFactorAmount.precision,
  );

  if (itemDetails.quantityBilled === 0) {
    return 'waitingForBilling';
  }

  switch (
    ezmoney.compare(itemDetails.amountBilledWithTax, expectedBilledAmount)
  ) {
    case 1:
      return 'overbilled';
    case 0:
      return 'billed';
    default:
      return 'partiallyBilled';
  }
};

export const areRequiredInvoiceLineItemsMissing = (
  purchaseOrder: PurchaseOrder | undefined,
  currentLineItems: LineItem[] = [],
): boolean => {
  if (
    !purchaseOrder ||
    !purchaseOrder.items ||
    purchaseOrder.items.length === 0
  ) {
    return false;
  }

  const purchaseOrderItemIds = purchaseOrder.items.map((item) => item.id);
  const isThereLineItemsLinkedToPurchaseOrder = currentLineItems.some(
    (lineItem) =>
      lineItem.purchaseOrderRequestItemId &&
      purchaseOrderItemIds.includes(lineItem.purchaseOrderRequestItemId),
  );

  return (
    !isThereLineItemsLinkedToPurchaseOrder &&
    purchaseOrder.areInvoiceLineItemsRequired
  );
};
